<template>
  <div>
    <!-- LOGO -->
    <NuxtLink
      v-animate-in-from-bottom
      :to="localePath('/')"
      :aria-label="$t('aria_labels.home')"
      class="absolute left-container top-10 z-40 mb-1 block h-6 text-blue-500"
    >
      <IconLogo class="h-5" />
    </NuxtLink>

    <div
      id="navigationMobile"
      ref="element"
      v-animate-zoom-in-out
      class="
        navigation-mobile fixed right-5 top-5 z-[51] block size-16 max-h-svh grow-0 overflow-hidden
        rounded-[200px] bg-blue-500 text-white transition-colors duration-300
      "
    >
      <!-- BURGER BUTTON -->
      <button
        ref="burgerButton"
        type="button"
        class="absolute inset-0 flex items-center justify-center"
        :aria-label="$t('aria_labels.open_navigation')"
        aria-controls="navigationMobile"
        :aria-expanded="isOpen"
        @click="toggle()"
      >
        <IconBurger class="w-8 text-white" />
      </button>

      <Container
        :class="isOpen ? 'pointer-events-auto' : 'pointer-events-none'"
        :aria-hidden="!isOpen"
      >
        <div
          ref="container"
          class="
            flex h-[90svh] max-h-[90svh] w-[calc(100vw-(100vw-100%))] flex-col overflow-hidden pb-14 pt-9
          "
        >
          <!-- HEADER -->
          <div class="mb-14 flex items-center justify-between">
            <!-- Logo -->
            <NuxtLink
              :to="localePath('/')"
              :aria-label="$t('aria_labels.home')"
              class="slide-up mb-1 block h-6"
            >
              <IconLogo class="h-5 sm:h-6" />
            </NuxtLink>

            <!-- Close Button -->
            <button
              ref="closeButton"
              :aria-label="$t('close')"
              type="button"
              class="group invisible flex items-center gap-3"
              aria-controls="navigationMobile"
              @click="closeAllFast()"
            >
              <IconPlus class="w-10 rotate-45" />
            </button>
          </div>

          <!-- CONTENT -->
          <nav ref="navElement" class="max-h-full overflow-hiden px-container pb-6 md:px-container-lg">
            <ul class="">
              <!-- NAVIGATION -->
              <li
                v-for="item in globals?.main.tree"
                :id="`nav-mobile-${item.page.id}`"
                :key="item.page.id"
                class="level1"
              >
                <div class="slide-up mb-4 flex items-center justify-between">
                  <Component
                    :is="buttonOrNuxtLink(selectedNavItem === item || !item.children?.length ? 'nuxt-link' : 'button')"
                    v-bind="selectedNavItem === item || !item.children?.length ? { to: item.page.url, target: item.page.url ? useIsExternalUrl(item.page.url) ? '_blank' : '_self' : null } : { onClick: () => item.children?.length ? enter(item) : null, ariaExpanded: item.children?.length ? selectedNavItem === item : undefined, ariaControls: item.children?.length ? `sub-nav-mobile-${item.page.id}` : undefined }"
                    class="typo-heading-sm"
                    :class="useIsRouterLinkActive(item.page.url, route.path)"
                  >
                    {{ item.page.title }}
                  </Component>

                  <button
                    v-if="item.children?.length"
                    :aria-expanded="item.children?.length ? selectedNavItem === item : undefined"
                    :aria-controls="item.children?.length ? `sub-nav-mobile-${item.page.id}` : undefined"
                    type="button"
                    class="pl-[10%] xs:px-[10%]"
                    @click="item.children?.length ? enter(item) : null"
                  >
                    <Transition name="fade" mode="out-in">
                      <IconPlus v-if="selectedNavItem !== item" class="size-[1.5em]" />
                      <IconMinus v-else class="size-[1.5em]" />
                    </Transition>
                  </button>
                </div>

                <ul
                  :id="`sub-nav-mobile-${item.page.id}`"
                  class="h-0 max-h-[45vh] space-y-5 overflow-hidden typo-body-xl"
                >
                  <li v-for="{ page } in item.children" :key="page.id" class="slide-up-sub first:mt-3">
                    <NuxtLink :to="page.url">
                      {{ page.title }}
                    </NuxtLink>
                  </li>
                </ul>
              </li>
            </ul>

            <!-- META NAVIGATION -->
            <ul ref="metaNavigation" class="level1 space-y-4 typo-body-xl">
              <li
                v-for="{ page } in globals?.main_meta.tree"
                :key="page.id"
                class="slide-up first:mt-4 last:mb-10"
              >
                <NuxtLink :to="page.url">
                  {{ page.title }}
                </NuxtLink>
              </li>

              <li>
                <!-- Search -->
                <NuxtLink :to="localePath('search')" class="slide-up flex items-center gap-3 py-3">
                  <IconMagnifyingGlass class="size-[1.25em]" />
                  <span>{{ $t('search.search') }}</span>
                </NuxtLink>
              </li>
            </ul>
          </nav>

          <!-- ACTIONS -->
          <div
            ref="actions"
            class="
              mt-auto flex flex-wrap items-center justify-between gap-4 px-container slide-up
              md:px-container-lg
            "
          >
            <LangSwitcherMobile />

            <div ref="portalButton" class="flex h-container-lg">
              <a
                v-if="globals?.general.portal_link"
                :href="globals?.general.portal_link"
                class="
                  flex h-full items-center justify-center rounded-full bg-white px-6 text-blue-500
                  transition-colors typo-button-text-md
                  hover:bg-blue-600
                "
              >
                {{ globals?.general.portal_link_text }}
              </a>
            </div>
          </div>

          <!-- BACK BUTTON -->
          <div ref="backButton" class="invisible mt-auto hidden translate-x-10 pt-5">
            <button
              type="button"
              class="mx-container flex w-fit items-center gap-4 md:mx-container-lg"
              @click="leave()"
            >
              <IconArrowDown class="size-[1.5em] rotate-90" />
              <span class="typo-body-xl">
                {{ $t('back') }}
              </span>
            </button>
          </div>
        </div>
      </Container>
    </div>
  </div>
</template>

<script lang="ts" setup>
import gsap from 'gsap'
import { useScrollLock } from '@vueuse/core'

const globals = inject(GlobalsKey, null)
const localePath = useLocalePath()
const route = useRoute()

const element = ref<HTMLElement | null>(null)
const container = ref<HTMLElement | null>(null)
const navElement = ref<HTMLElement | null>(null)
const burgerButton = ref<HTMLElement | null>(null)
const closeButton = ref<HTMLElement | null>(null)
const backButton = ref<HTMLElement | null>(null)
const actions = ref<HTMLElement | null>(null)
const isOpen = ref(false)
const selectedNavItem = ref(null)
const body = ref<HTMLElement | null>(null)
let isLocked: any

async function toggle() {
  if (!isOpen.value) {
    show()
  } else {
    hide()
  }
}

let tlCloseFast: gsap.core.Timeline | null = null
async function closeAllFast() {
  // On route change, the elements can be not available
  if (!container.value || !element.value || !burgerButton.value) return

  tlCloseFast = gsap.timeline({
    defaults: {
      duration: 0.4,
      ease: 'power1.inOut',
    },
  })

  // Animate the container to close resetting the initial state
  await tlCloseFast.to(container.value, {
    opacity: 0,
    display: 'none',
  }).to(element.value, {
    width: '4rem',
    height: '4rem',
    top: '1.25rem',
    right: '1.25rem',
    minHeight: '0',
    borderRadius: '200px',
  }, '-=0.2')
    .to(burgerButton.value, {
      autoAlpha: 1,

    }, '-=0.1')

  // Revert and kill the timelines
  tlCloseFast.revert()
  tlCloseFast.kill()

  if (tlEnter) {
    tlEnter.revert()
    tlEnter.kill()
  }

  if (tl) {
    tl.revert()
    tl.kill()
  }

  selectedNavItem.value = null
  isOpen.value = false
  isLocked.value = false
}

let tl: gsap.core.Timeline | null = null
async function show() {
  if (!element.value) return

  const slideUpItems = element.value?.querySelectorAll('.slide-up')

  // Create or reset the timeline
  tl = gsap.timeline({
    defaults: {
      duration: 0.6,
      ease: 'power1.inOut',
    },
  })

  tl
    .to(burgerButton.value, {
      autoAlpha: 0,
      duration: 0.3,
    })
    .to(element.value, { borderRadius: '0 0 0 3.75rem' }, '<')
    .call(() => {
      isOpen.value = true
    }, null, '<')
    .to(element.value, {
      top: 0,
      right: 0,
    }, '<')
    .to(element.value, { width: '100dvw' }, '<')
    .to(element.value, { minHeight: '90svh' }, '<')
    .to(element.value, { height: 'auto' }, '<')
    .to(closeButton.value, {
      autoAlpha: 1,
      duration: 0.3,
    }, '-=0.1')
    .to(slideUpItems, {
      autoAlpha: 1,
      translateY: 0,
      duration: 0.3,
      stagger: 0.1,
    }, '-=0.2')
    .set(navElement.value, {
      overflowY: 'auto',
    })

  await tl
  isLocked.value = true
}

async function hide() {
  if (!tl) {
    return
  }

  if (tlEnter) {
    tlEnter.timeScale(2)
    await tlEnter.reverse()
  }

  // Wait for the timeline to finish if it's active
  if (tl.isActive()) {
    await new Promise((resolve) => {
      const checkActive = () => {
        if (!tl.isActive()) {
          resolve()
        } else {
          setTimeout(checkActive, 50) // Check every 50ms
        }
      }
      checkActive()
    })
  }
  // Reverse the timeline
  tl.timeScale(2)
  await tl.reverse()
  tl.revert()

  selectedNavItem.value = null
}

let tlEnter: gsap.core.Timeline | null = null

async function enter(item: any) {
  if (selectedNavItem.value === item) {
    leave()
    return
  }

  tlEnter = gsap.timeline({
    defaults: {
      duration: 0.5,
      ease: 'power1.inOut',
    },
  })

  selectedNavItem.value = item

  // Get the current subnav element
  const subNav = document.getElementById(`sub-nav-mobile-${item.page.id}`)
  if (!subNav) return

  // Get the elements to animate
  const level1 = element.value?.querySelectorAll(`.level1:not(#nav-mobile-${item.page.id})`)
  const slideUpItems = subNav?.querySelectorAll('.slide-up-sub')

  tlEnter
    .to(level1, {
      translateX: '-125%',
      stagger: 0.1,
    })
    .to(actions.value, {
      autoAlpha: 0,
      translateY: '2.75rem',
      display: 'none',
    }, '<')
    .to(level1, {
      autoAlpha: 0,
      height: 0,
      overflow: 'hidden',
    }, '-=0.2')
    .to(subNav, {
      height: 'auto',
    }, '<')
    .to(slideUpItems, {
      autoAlpha: 1,
      translateY: 0,
      stagger: 0.1,
    }, '<')
    .to(backButton.value, {
      display: 'block',
      autoAlpha: 1,
      translateX: 0,
    }, '-=0.3')
    .set(subNav, {
      overflowY: 'auto',
    })
}

async function leave() {
  selectedNavItem.value = null
  tlEnter?.timeScale(1.5)
  await tlEnter?.reverse()
  tlEnter?.timeScale(1)
  tlEnter?.revert()
}

function buttonOrNuxtLink(type: 'button' | 'nuxt-link') {
  if (type === 'nuxt-link') {
    return resolveComponent('NuxtLink')
  }
  return 'button' as const
}

// Run hide() after every route change
const router = useRouter()
router.afterEach(() => {
  if (isOpen.value) closeAllFast()
})

onMounted(() => {
  body.value = document.body
  isLocked = useScrollLock(body)
})

onUnmounted(() => {
  if (isLocked.value) {
    isLocked.value = false
  }

  tl?.kill() // Kill the timeline
  tlEnter?.kill() // Kill the timeline
})
</script>

<style>
.navigation-mobile {
  .slide-up {
    @apply invisible;
    transform: translateY(2.5rem);
  }

  .slide-up-sub {
    @apply invisible;
    transform: translateY(2.5rem);
  }

  .burger-button {
    @apply visible;
  }

  .slide-down {
    @apply invisible;
    transform-box: fill-box;
    transform: translateY(-2.5rem);
  }

  .router-link-active {
    text-decoration-line: underline;
    text-underline-offset: 0.4rem;
    text-decoration-thickness: 1px;
  }

  .router-link-exact-active {
    text-decoration-line: underline;
    text-underline-offset: 0.4rem;
    text-decoration-thickness: 1px;
  }
}
</style>
